import { useCallback, useMemo, useState } from "react"

import { useParams } from "react-router-dom"

import { useGeoServerFeaturesList, useGeoServerRetrieveFeature } from "@l2r-front/l2r-geodata"
import { Draw, REFERENTIAL_MAP_ID } from "@l2r-front/l2r-map"
import { NetworkReferentialLayer, ReferentialMap, useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { UiModal, useIsMobileDevice } from "@l2r-front/l2r-ui"

import { roadworksQueryKeys } from "../../hooks/queries/queryKeys"
import { parametricsToLinearLocations } from "../../utils/parametricsToLinearLocations"

import * as Styled from "./RoadworkSectionPainter.styled"

export function RoadworkSectionPainter(props) {

    const {
        cancelButtonLabel,
        className,
        desc,
        onClose,
        open,
        onSave,
        saveButtonLabel,
        title,
    } = props

    const [saveEnable, setSaveEnable] = useState(false)
    const [linearLocationsToSave, setLinearLocationsToSave] = useState([])
    const { selectedNetwork } = useNetworksStateContext()
    const { road, roadworkUuid } = useParams()
    const isMobile = useIsMobileDevice()

    const queryParams = {
        layer: selectedNetwork.referential.layerSection,
        linear_location_road: road,
    }
    const { data: roadFeatures } = useGeoServerFeaturesList(
        ["paintingRoad", queryParams],
        {
            ...queryParams,
            as_geojson: true,
        },
        {
            enabled: !!road && !!selectedNetwork.referential.layerSection,
        },
    )

    const { data: sectionSegments } = useGeoServerRetrieveFeature(roadworkUuid, roadworksQueryKeys.section(roadworkUuid), { as_geojson: true }, {})

    const handleFeaturesChanged = useCallback(featureCollection => {
        const linearLocations = parametricsToLinearLocations(roadFeatures, featureCollection.features)

        setSaveEnable(linearLocations.length > 0)
        setLinearLocationsToSave(linearLocations)
    }, [roadFeatures])

    const handleSave = useCallback(() => {
        if (linearLocationsToSave.length) {
            onSave({
                linearLocation: linearLocationsToSave.map(ll => ({
                    typeid: 1,
                    road: road,
                    start: ll[0],
                    end: ll[1],
                })),
            })
            return
        }
        onClose()
    }, [onClose, onSave, linearLocationsToSave, road])

    const buttons = useMemo(() => {
        return <>
            <Styled.CTAButton id={"linear-edit-cancel"} isMobile={isMobile} variant="outlined" onClick={onClose}>{cancelButtonLabel}</Styled.CTAButton>
            <Styled.CTAButton id={"linear-edit-ok"} isMobile={isMobile} disabled={!saveEnable} onClick={handleSave}>{saveButtonLabel}</Styled.CTAButton>
        </>
    }, [cancelButtonLabel, handleSave, isMobile, onClose, saveEnable, saveButtonLabel])

    if (!roadFeatures) {
        return null
    } else {
        return (
            <UiModal
                buttons={buttons}
                className={className}
                desc={desc}
                open={open}
                onClose={onClose}
                title={title}
            >
                <ReferentialMap>
                    <NetworkReferentialLayer interactive={false} mapId={REFERENTIAL_MAP_ID} />
                    <Draw
                        opts={{ roadSegments: roadFeatures, startingFeatures: sectionSegments }}
                        onFeaturesChange={handleFeaturesChanged} />
                </ReferentialMap>
            </UiModal>
        )
    }
}

RoadworkSectionPainter.propTypes = {
    className: PropTypes.string,
    cancelButtonLabel: PropTypes.string.isRequired,
    desc: PropTypes.string,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    saveButtonLabel: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
}
